import { LocalHospital, MonitorHeart } from "@mui/icons-material";
import VaccinesIcon from "@mui/icons-material/Vaccines";
import {
  alpha,
  Box,
  Card,
  CardContent,
  Chip,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { trimText } from "../../../../../../../utils/Functions";

function toTime(date) {
  const parsed = date ? dayjs(date, "DD/MM/YYYY") : null;
  return parsed?.isValid() ? parsed.valueOf() : -Infinity;
}

export default function TreatmentDetailsSummary({ data }) {
  const theme = useTheme();
  const { t } = useTranslation();

  const getCategoryColor = (category) =>
    String(category).toUpperCase() === "RABIES" ? "error" : "primary";
  const ampouleDisplay =
    data?.ampoule && data.ampoule.trim() !== "" ? data.ampoule : "-";

  const corporalDisplay =
    data == null || data.corporal === null
      ? "-"
      : data.corporal
        ? t("Yes")
        : t("No");

  const corporalColor =
    data == null || data.corporal === null
      ? "primary"
      : data.corporal
        ? "primary"
        : "warning";

  // Newest first; dates come as DD/MM/YYYY and undated vaccines go last
  const vaccines = [...(data?.vaccines || [])].sort(
    (a, b) => toTime(b?.date) - toTime(a?.date),
  );
  const antiparasites = data?.antiparasites || [];

  return (
    <Card sx={{ borderRadius: 3, height: "100%" }}>
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
          <VaccinesIcon color="primary" sx={{ fontSize: 28 }} />
          <Typography variant="h5" fontWeight={700}>
            {t("TreatmentDetails")}
          </Typography>
        </Stack>

        <Stack spacing={4}>
          {/* CSS grid instead of Grid: Grid's negative margins get reset inside a Stack and overflow */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
              gap: 3,
              "& > *": { minWidth: 0 },
            }}
          >
            <SummaryTable
              icon={<VaccinesIcon />}
              title={t("Vaccines")}
              emptyMessage={t("NoVaccinesRecorded")}
              columns={["Date", "Name", "Category"]}
              rows={vaccines.map((vaccine) => [
                <Typography variant="body2" noWrap>
                  {vaccine.date || "-"}
                </Typography>,
                <Tooltip title={vaccine.name}>
                  <span>{trimText(vaccine.name, 35)}</span>
                </Tooltip>,
                <Chip
                  size="small"
                  label={t(
                    String(vaccine.category).toUpperCase() === "RABIES"
                      ? "Rabies"
                      : "Other",
                  )}
                  color={getCategoryColor(vaccine.category)}
                  sx={{ fontWeight: 500, borderRadius: 2 }}
                />,
              ])}
            />
            <SummaryTable
              icon={<LocalHospital />}
              title={t("Antiparasitics")}
              emptyMessage={t("NoAntiparasiticsRecorded")}
              columns={["Name"]}
              rows={antiparasites.map((item) => [
                <Tooltip title={item}>
                  <span>{trimText(item, 35)}</span>
                </Tooltip>,
              ])}
            />
          </Box>

          {/* Other section (Ampoule & Corporal) */}
          <Box>
            <Typography
              variant="h6"
              fontWeight={600}
              gutterBottom
              sx={{ display: "flex", alignItems: "center", gap: 1 }}
            >
              <MonitorHeart /> {t("Other")}
            </Typography>
            <Paper
              sx={{
                p: 3,
                background: `linear-gradient(135deg, ${alpha(
                  theme.palette.background.default,
                  0.8,
                )} 0%, ${alpha(theme.palette.background.paper, 0.6)} 100%)`,
                border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                borderRadius: 3,
              }}
            >
              <Stack spacing={2}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="body1" color="text.secondary">
                    {t("Ampoule")}
                  </Typography>
                  <Tooltip title={ampouleDisplay}>
                    <Chip
                      label={trimText(ampouleDisplay, 35)}
                      color="primary"
                      sx={{ fontWeight: 700 }}
                    />
                  </Tooltip>
                </Stack>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="body1" color="text.secondary">
                    {t("CollectForCoprological")}
                  </Typography>
                  <Chip
                    label={corporalDisplay}
                    color={corporalColor}
                    variant="contained"
                    sx={{ fontWeight: 600, color: "white" }}
                  />
                </Stack>
              </Stack>
            </Paper>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

const ROW_HEIGHT = 40;
const MAX_VISIBLE_ROWS = 6;

// Outlined table in the style of WarehouseTable; rows are arrays of cell contents.
// Shows at most MAX_VISIBLE_ROWS rows, the rest scroll under a sticky header.
function SummaryTable({ icon, title, emptyMessage, columns = [], rows = [] }) {
  const { t } = useTranslation();
  return (
    <Box>
      <Typography
        variant="h6"
        fontWeight={600}
        gutterBottom
        sx={{ display: "flex", alignItems: "center", gap: 1 }}
      >
        {icon}
        {title}
      </Typography>
      <Paper
        variant="outlined"
        sx={{ borderRadius: 3, borderColor: "divider", overflow: "hidden" }}
      >
        <TableContainer
          sx={{ maxHeight: ROW_HEIGHT * (MAX_VISIBLE_ROWS + 1) + 2 }}
        >
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow
                sx={{
                  height: ROW_HEIGHT,
                  "& th": {
                    bgcolor: (theme) =>
                      theme.palette.mode === "light"
                        ? theme.palette.grey[50]
                        : theme.palette.grey[900],
                    fontWeight: 700,
                    fontSize: "0.75rem",
                    textTransform: "uppercase",
                    letterSpacing: "0.04em",
                    color: "text.secondary",
                    borderBottom: "2px solid",
                    borderBottomColor: "divider",
                  },
                }}
              >
                <TableCell sx={{ width: 48 }} />
                {columns.map((column) => (
                  <TableCell key={column}>{t(column)}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={columns.length + 1}
                    sx={{ border: 0, py: 3 }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      {emptyMessage}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((cells, index) => (
                  <TableRow key={index} hover sx={{ height: ROW_HEIGHT }}>
                    <TableCell sx={{ color: "text.disabled" }}>
                      {index + 1}
                    </TableCell>
                    {cells.map((cell, cellIndex) => (
                      <TableCell key={cellIndex}>{cell}</TableCell>
                    ))}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}
